import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { Alert } from 'react-bootstrap';
import type { KeyboardEvent } from 'react';
import type { InjectedRouter } from 'react-router';

import { window } from '../../../globals';
import { onEnter } from '../../../utils';
import { LoaderButton } from '../../generic/Button';
import { FormGroup, LabeledTextInput } from '../../generic/Forms';
import Redirect from '../../generic/Redirect';
import Text from '../../generic/Text';
import useOrgSetup from '../context/useOrgSetup';
import AuthForm from '../form';
import CenteredLayout from '../layouts/Centered';
import AccessGranted from '../onboarding/AccessGranted';
import AccessPending from '../onboarding/AccessPending';
import ModalRequestOrgAccess from '../onboarding/ModalRequestOrgAccess';
import OrgCreateOrJoinSelector from '../onboarding/OrgCreateOrJoinSelector';

type OnboardingProps = {
  router: InjectedRouter;
};

/**
 * Represents the new auth Onboarding flow.
 * Really it's just one step where the user confirms which org they are
 * creating or (if on-prem) which org they are joining.
 */
const Onboarding = ({ router }: OnboardingProps) => {
  const {
    isOnPrem,
    isSubmitDisabled,
    status,
    email,
    orgTitle,
    orgNameInputRef,
    joinableOrgs,
    orgToJoin,
    setOrgToJoin,
    showCreateOrJoinSelector,
    showOrganizationPicker,
    toggleShowOrganizationPicker,
    requestOrgAccessModal,
    accessRequest,
    onCancelAccessRequest,
    onSubmit,
  } = useOrgSetup(router);

  if (window.user?.organizationId) {
    return (
      <Redirect to="/projects" />
    );
  }

  if (accessRequest.state === 'pending') {
    return (
      <AccessPending
        orgTitle={accessRequest.orgTitle}
        isLoading={status.loading}
        onCancelAccessRequest={onCancelAccessRequest}
      />
    );
  }

  if (accessRequest.state === 'granted') {
    return (
      <AccessGranted
        orgTitle={accessRequest.orgTitle}
        isLoading={status.loading}
        onCancelAccessRequest={onCancelAccessRequest}
      />
    );
  }
  return (
    <main className="fossa-auth">
      <CenteredLayout>
        <AuthForm.Container onSubmit={onSubmit}>
          <AuthForm.Heading
            heading={isOnPrem
              ? 'Organization Setup'
              // Any cloud user seeing this page is creating a new org.
              : 'New Organization Setup'}
          />
          {status.error && (
            <Alert bsStyle="error">
              {status.error}
            </Alert>
          )}
          {showCreateOrJoinSelector && (
            <OrgCreateOrJoinSelector
              showOrganizationPicker={showOrganizationPicker}
              toggleShowOrganizationPicker={toggleShowOrganizationPicker}
            />
          )}
          {showOrganizationPicker ? (
            <div className="margin-bottom-md">
              <div className="margin-bottom-sm">
                <Text>
                  Select which company in FOSSA to join as a user
                </Text>
              </div>
              <Select
                value={orgToJoin}
                onChange={event => setOrgToJoin(event.target.value as number)}
                style={{ width: '100%' }}
              >
                {joinableOrgs.map(({ id, title }) => (
                  <MenuItem key={id} value={id}>
                    {title}
                  </MenuItem>
                ))}
              </Select>
            </div>
          ) : (
            <FormGroup>
              <LabeledTextInput
                labelVariant="legacy"
                type="email"
                label="Admin Email"
                onChange={email.onChange}
                value={email.value}
              />
              <LabeledTextInput
                labelVariant="legacy"
                ref={orgNameInputRef}
                type="text"
                label="Organization Name"
                onChange={orgTitle.onChange}
                value={orgTitle.value}
                onKeyDown={onEnter((event: KeyboardEvent<HTMLInputElement>) => {
                  event.preventDefault();
                  onSubmit();
                })}
              />
            </FormGroup>
          )}
          <LoaderButton
            type="primary"
            size="large"
            onClick={onSubmit}
            isDisabled={isSubmitDisabled}
            isLoading={status.loading}
            isFullWidth
          >
            Start using FOSSA
          </LoaderButton>
        </AuthForm.Container>
        <ModalRequestOrgAccess
          isShown={requestOrgAccessModal.isShown}
          orgTitle={requestOrgAccessModal.orgTitle}
          onHide={requestOrgAccessModal.onHide}
          onRequestAccess={requestOrgAccessModal.onRequestAccess}
        />
      </CenteredLayout>
    </main>
  );
};

export default Onboarding;
